import { History, Mail } from 'lucide-react';
import React from 'react';

/**
 * Contact History Section Component
 * Based on conversation-mockup.html design
 * Displays candidate's previous contact information
 *
 * @param {Object} props
 * @param {Object} props.contactHistory - Contact history data
 */
export function ContactHistorySection({ contactHistory }) {
  if (!contactHistory || !contactHistory.contacted) {
    return null;
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
      <h4 className="font-semibold text-gray-900 mb-2 text-sm flex items-center gap-2">
        <History className="w-3.5 h-3.5 text-yellow-600" />
        Contact History
      </h4>

      <div className="space-y-2">
        {/* Status Badge */}
        <div className="flex items-center gap-2">
          <span className="px-2 py-1 bg-yellow-600 text-white text-xs font-medium rounded-full">
            Previously Contacted
          </span>
          {contactHistory.lastContactDate && (
            <span className="text-xs text-gray-600">
              Last contacted: {formatDate(contactHistory.lastContactDate)}
            </span>
          )}
        </div>

        {/* Contact Details Grid */}
        <div className="grid grid-cols-2 gap-3 text-xs mt-3">
          {contactHistory.lastContactMethod && (
            <div>
              <p className="text-gray-500 mb-0.5">Contact Method</p>
              <p className="font-medium text-gray-900 flex items-center gap-1">
                <Mail className="w-3 h-3 text-green-600" />
                {contactHistory.lastContactMethod}
              </p>
            </div>
          )}

          {contactHistory.lastJobTitle && (
            <div>
              <p className="text-gray-500 mb-0.5">For Position</p>
              <p className="font-medium text-gray-900">{contactHistory.lastJobTitle}</p>
            </div>
          )}

          {contactHistory.lastJobId && (
            <div className="col-span-2">
              <p className="text-gray-500 mb-0.5">Job ID</p>
              <p className="font-medium text-gray-900 text-xs font-mono">
                {contactHistory.lastJobId}
              </p>
            </div>
          )}
        </div>

        {/* Communication Timeline */}
        {contactHistory.raw_communication_data && (
          <div className="mt-3 pt-3 border-t border-yellow-200">
            <p className="text-xs font-semibold text-gray-700 mb-2">Communication Timeline:</p>
            <div className="space-y-1.5">
              {/* Email History */}
              {contactHistory.raw_communication_data.email?.history?.map((email, index) => (
                <div key={`email-${index}`} className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-yellow-600 rounded-full mt-1.5"></div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-900">
                      Email sent: {formatDate(email.timestamp)} {formatTime(email.timestamp)}
                    </p>
                    {email.subject && (
                      <p className="text-xs text-gray-500">Subject: "{email.subject}"</p>
                    )}
                  </div>
                </div>
              ))}

              {/* Call History */}
              {contactHistory.raw_communication_data.calls?.history?.map((call, index) => (
                <div key={`call-${index}`} className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-green-600 rounded-full mt-1.5"></div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-900">
                      Call made: {formatDate(call.timestamp)} {formatTime(call.timestamp)}
                    </p>
                    {call.status && (
                      <p className="text-xs text-gray-500">Status: {call.status}</p>
                    )}
                  </div>
                </div>
              ))}

              {/* Last Contact Date from Stats */}
              {contactHistory.raw_communication_data.stats?.lastContactDate && (
                <div className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-1.5"></div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-900">
                      Last activity: {formatDate(contactHistory.raw_communication_data.stats.lastContactDate)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ContactHistorySection;

